// WithdrawalSheet.jsx
import React, { useState, useEffect, useRef } from 'react';

export function WithdrawalSheet({ isVisible, onClose, onConfirm, amount }) {
    const [selectedMethod, setSelectedMethod] = useState('balance');
    const [withdrawalAmount, setWithdrawalAmount] = useState(amount);
    const [startY, setStartY] = useState(null);
    const [currentY, setCurrentY] = useState(null);
    const sheetRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        setWithdrawalAmount(amount);
    }, [amount]);

    useEffect(() => {
        if (isVisible) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('sheet-open');
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        } else {
            document.body.style.overflow = '';
            document.body.classList.remove('sheet-open');
        }

        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('sheet-open');
        };
    }, [isVisible]);

    const handleTouchStart = (e) => {
        setStartY(e.touches[0].clientY);
    };

    const handleTouchMove = (e) => {
        if (startY === null) return;

        const currentTouchY = e.touches[0].clientY;
        const diff = currentTouchY - startY;

        if (diff > 0) {
            e.preventDefault();
            setCurrentY(diff);

            if (sheetRef.current) {
                const maxTranslate = window.innerHeight * 0.5;
                const translateY = Math.min(diff, maxTranslate);
                sheetRef.current.style.transform = `translateY(${translateY}px)`;
            }
        }
    };

    const handleTouchEnd = () => {
        if (startY === null) return;

        const diff = currentY || 0;

        if (diff > 100) {
            onClose();
        }

        if (sheetRef.current) {
            sheetRef.current.style.transform = '';
        }

        setStartY(null);
        setCurrentY(null);
    };

    const handleMouseDown = (e) => {
        setStartY(e.clientY);

        const handleMouseMove = (e) => {
            if (startY === null) return;

            const diff = e.clientY - startY;

            if (diff > 0) {
                setCurrentY(diff);

                if (sheetRef.current) {
                    const maxTranslate = window.innerHeight * 0.5;
                    const translateY = Math.min(diff, maxTranslate);
                    sheetRef.current.style.transform = `translateY(${translateY}px)`;
                }
            }
        };

        const handleMouseUp = () => {
            const diff = currentY || 0;

            if (diff > 100) {
                onClose();
            }

            if (sheetRef.current) {
                sheetRef.current.style.transform = '';
            }

            setStartY(null);
            setCurrentY(null);

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleAmountChange = (e) => {
        let value = e.target.value.replace(/[^\d]/g, '');

        if (value === '') {
            setWithdrawalAmount(0);
            return;
        }

        let numValue = parseInt(value, 10);

        if (numValue > amount) {
            numValue = amount;
        }

        setWithdrawalAmount(numValue);
    };

    const handleMaxClick = () => {
        setWithdrawalAmount(amount);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleConfirm = () => {
        if (withdrawalAmount <= 0) return;
        if (withdrawalAmount > amount) return;
        onConfirm(selectedMethod, withdrawalAmount);
        onClose();
    };

    // Форматирование суммы с разделителями тысяч
    const formatAmountWithSpaces = (value) => {
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };

    return (
        <div className={`withdrawal-sheet-wrapper ${isVisible ? 'visible' : ''}`}>
            <div className="sheet-overlay" onClick={onClose} />
            <div ref={sheetRef} className="withdrawal-sheet">
                <div className="sheet-content">
                    <div
                        className="sheet-drag-handle"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                    >
                        <div className="drag-indicator" />
                    </div>

                    <h3 className="sheet-title">Вывод средств</h3>

                    {/* Блок суммы */}
                    <div className="amount-section">
                        <div className="amount-label">Сумма вывода</div>

                        <div className="amount-input-container">
                            {/*<span className="currency-symbol">₽</span>*/}
                            <input
                                ref={inputRef}
                                type="tel"
                                inputMode="numeric"
                                className="amount-input"
                                value={withdrawalAmount === 0 ? '' : withdrawalAmount}
                                onChange={handleAmountChange}
                                placeholder="0"
                            />
                        </div>

                        <div className="amount-actions">
                            <button className="max-amount-btn" onClick={handleMaxClick}>
                                Вся сумма
                            </button>
                            <div className="available-amount">
                                Доступно: <span>{formatAmountWithSpaces(amount)} ₽</span>
                            </div>
                        </div>
                    </div>

                    {/* Выбор метода */}
                    <div className="methods-container">
                        <div
                            className={`method-item ${selectedMethod === 'balance' ? 'selected' : ''}`}
                            onClick={() => setSelectedMethod('balance')}
                        >
                            <div className="method-info">
                                <div className="method-name">На баланс бота</div>
                                <div className="method-desc">Средства поступят на ваш баланс(MIN. 500₽)</div>
                            </div>
                        </div>

                        <div
                            className={`method-item ${selectedMethod === 'cryptobot' ? 'selected' : ''}`}
                            onClick={() => setSelectedMethod('cryptobot')}
                        >
                            <div className="method-info">
                                <div className="method-name">CryptoBot</div>
                                <div className="method-desc">Вывод чеком Cryptobot(MIN. 500₽)</div>
                            </div>
                        </div>
                    </div>

                    <div className="sheet-buttons">
                        <button className="sheet-button sheet-button-cancel" onClick={onClose}>
                            Отмена
                        </button>
                        <button
                            className="sheet-button btn-green"
                            onClick={handleConfirm}
                            disabled={withdrawalAmount <= 0 || withdrawalAmount > amount}
                        >
                            Подтвердить вывод
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}